'use client';

import { FC, useEffect } from 'react';

import { AddressLocation, AddressMapLatLng } from '@ravago/shared/page-data/models/elements';

import { Map, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';

import { convertStringToLatLng } from '../../../../utils/location/location.utils';

interface AddressMapGoogleProps {
   latLng: AddressMapLatLng;
   locations: AddressLocation[];
   defaultZoom?: number;
   height?: number;
   activeMarkerLocation?: AddressLocation;
   children?: React.ReactNode[];
}

export const AddressMapGoogle: FC<AddressMapGoogleProps> = ({ latLng, locations, defaultZoom, height, activeMarkerLocation, children }) => {
   const apiIsLoaded = useApiIsLoaded();
   const map = useMap();

   useEffect(() => {
      if (!locations?.length || !map || !apiIsLoaded) return;

      const bounds = new window.google.maps.LatLngBounds();

      if (activeMarkerLocation) {
         const activeMarkerLocationLatLng = convertStringToLatLng(activeMarkerLocation.latLng);
         if (activeMarkerLocationLatLng) bounds.extend(activeMarkerLocationLatLng);
      } else {
         locations.forEach((location) => {
            const locationLatLng = convertStringToLatLng(location.latLng);
            if (locationLatLng) bounds.extend(locationLatLng);
         });
      }

      map.fitBounds(bounds);

      const currentZoom = map.getZoom() ?? defaultZoom ?? 15;

      if ((defaultZoom && currentZoom > defaultZoom && locations.length === 1) || activeMarkerLocation) {
         // If there is only one location and the zoom is greater than the default, set it to default
         // This is to ensure that the map does not zoom in too much on a single location
         map.setZoom(defaultZoom ?? 15);
      }
   }, [map, apiIsLoaded, locations, activeMarkerLocation]);

   return (
      <Map
         mapId="The-MAP"
         style={{ width: '100%', height: height ? `${height}px` : '400px' }}
         defaultCenter={latLng}
         defaultZoom={defaultZoom}
         gestureHandling="cooperative"
         renderingType="RASTER"
         reuseMaps
         disableDefaultUI
         zoomControl
      >
         {children}
      </Map>
   );
};
